import { useMemo, useRef } from "react";
import { Card, CardBody, CardHeader } from "reactstrap";
import { ColumnDef } from "@tanstack/react-table";
import TableContainer from "components/common/TableContainer";
import Avatar from "components/common/Avatar";
import { getClockStatusList } from "features/EmployeeAttendance/services/EmployeeAttendanceService";
import { AttendanceClockStatus } from "features/EmployeeAttendance/constants/employee-attendance.constants";
import type { ClockStatusListRow } from "features/EmployeeAttendance/types/employee-attendance.types";
import moment from "moment";

function resolveName(row: ClockStatusListRow) {
  const first = row.user?.firstName ?? "";
  const last = row.user?.lastName ?? "";
  return [first, last].filter(Boolean).join(" ").trim() || row.code || "Unknown";
}

const CLOCK_STATUS_CONFIG: Record<
  AttendanceClockStatus,
  { label: string; color: string }
> = {
  [AttendanceClockStatus.CLOCKED_IN]: { label: "Clocked in", color: "success" },
  [AttendanceClockStatus.CLOCKED_OUT]: { label: "Clocked out", color: "secondary" },
  [AttendanceClockStatus.NOT_CLOCKED_IN]: { label: "Not clocked in", color: "danger" },
};

type Props = { refreshRef?: React.MutableRefObject<(() => void) | null> };

export function ActiveEmployeesCard({ refreshRef }: Props = {}) {
  const today = moment().format("YYYY-MM-DD");
  const internalRef = useRef<(() => void) | null>(null);
  const tableRef = refreshRef ?? internalRef;

  const requestParams = useMemo(
    () => ({ date: today }),
    [today],
  );

  const columns: ColumnDef<ClockStatusListRow>[] = useMemo(
    () => [
      {
        header: "Employee",
        id: "employee",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const name = resolveName(row.original);
          const avatar = row.original.user?.avatar ?? undefined;
          const code = row.original.code;
          return (
            <div className="d-flex align-items-center gap-2">
              <div className="flex-shrink-0 avatar-xs">
                <Avatar
                  name={name}
                  image={avatar}
                  className="rounded-circle w-100 h-100"
                />
              </div>
              <div className="min-w-0">
                <div className="fw-medium text-truncate small">{name}</div>
                {code ? (
                  <div className="text-muted" style={{ fontSize: "11px" }}>
                    {code}
                  </div>
                ) : null}
              </div>
            </div>
          );
        },
      },
      {
        header: "Status",
        id: "clockStatus",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const cs = row.original.clockStatus;
          const config = CLOCK_STATUS_CONFIG[cs] ?? { label: cs, color: "secondary" };
          const { label, color } = config;
          return (
            <span className={`badge bg-${color}-subtle text-${color} border border-${color}-subtle`}>
              {label}
            </span>
          );
        },
      },
    ],
    [],
  );

  return (
    <Card className="mb-4">
      <CardHeader className="d-flex align-items-center justify-content-between">
        <div>
          <h5 className="card-title mb-0">Active employees</h5>
          <p className="text-muted mb-0" style={{ fontSize: "0.75rem" }}>
            Today's clock-in status
          </p>
        </div>
        <i className="ri-team-line fs-20 text-muted" />
      </CardHeader>
      <CardBody>
        <TableContainer
          columns={columns}
          fetchApi={getClockStatusList}
          requestParams={requestParams}
          refreshRef={tableRef}
          syncPaginationToUrl={false}
          pageSize={10}
          globalSearch={false}
          hidePageSize
          divClass="table-responsive table-card mb-0"
          tableClass="table-nowrap align-middle mb-0"
          theadClass="table-light text-muted"
        />
      </CardBody>
    </Card>
  );
}
